import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Users, Search, Star, Award } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { usersAPI, coursesAPI, recommendationsAPI } from '../../lib/api';
import { toast } from 'sonner';

const roles = [
  { value: 'employee', label: 'Employee', color: 'bg-gray-100 text-gray-700' },
  { value: 'manager', label: 'Manager', color: 'bg-blue-100 text-blue-700' },
  { value: 'hr_admin', label: 'HR Admin', color: 'bg-purple-100 text-purple-700' },
  { value: 'super_admin', label: 'Super Admin', color: 'bg-red-100 text-red-700' },
];

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showRecommend, setShowRecommend] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [recCourse, setRecCourse] = useState('');
  const [recReason, setRecReason] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, coursesRes] = await Promise.all([usersAPI.getAll(), coursesAPI.getAll({ published_only: true })]);
        setUsers(usersRes.data);
        setCourses(coursesRes.data);
      } catch { toast.error('Failed to load'); }
    };
    fetchData();
  }, []);

  const filteredUsers = users.filter(u => {
    if (search && !u.name.toLowerCase().includes(search.toLowerCase()) && !u.email.toLowerCase().includes(search.toLowerCase())) return false;
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    return true;
  });

  const handleUpdateRole = async (userId, newRole) => {
    try { await usersAPI.updateRole(userId, newRole); toast.success('Updated!'); setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u)); }
    catch { toast.error('Failed'); }
  };

  const handleRecommend = async () => {
    if (!recCourse || !recReason) { toast.error('Fill all'); return; }
    try {
      await recommendationsAPI.assign({ course_id: recCourse, user_id: selectedUser.id, reason: recReason });
      toast.success('Recommended!'); setShowRecommend(false); setRecCourse(''); setRecReason('');
    } catch { toast.error('Failed'); }
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const getRoleColor = (role) => roles.find(r => r.value === role)?.color || 'bg-gray-100';

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="font-sketch text-4xl">Manage Users</h1>
          <p className="text-muted-foreground">View and manage roles</p>
        </div>

        <div className="card-sketchy flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} className="input-sketchy pl-10" placeholder="Search..." />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="input-sketchy w-40"><SelectValue placeholder="All Roles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {roles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {showRecommend && selectedUser && (
          <div className="card-sketchy space-y-4">
            <h3 className="font-sketch text-xl">Recommend to {selectedUser.name}</h3>
            <Select value={recCourse} onValueChange={setRecCourse}>
              <SelectTrigger className="input-sketchy"><SelectValue placeholder="Select course" /></SelectTrigger>
              <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={recReason} onChange={e => setRecReason(e.target.value)} className="input-sketchy" placeholder="Reason" />
            <div className="flex gap-2">
              <Button className="btn-sketchy" onClick={handleRecommend}>Send</Button>
              <Button variant="outline" className="border-2 border-black" onClick={() => setShowRecommend(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {filteredUsers.length > 0 ? (
          <div className="card-sketchy overflow-x-auto p-0">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-black">
                <tr>
                  <th className="text-left p-4">User</th>
                  <th className="text-left p-4">Department</th>
                  <th className="text-left p-4">Role</th>
                  <th className="text-left p-4">Points</th>
                  <th className="text-left p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => (
                  <tr key={u.id} className="border-b hover:bg-gray-50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10 border-2 border-black"><AvatarFallback className="bg-primary/10 font-bold">{getInitials(u.name)}</AvatarFallback></Avatar>
                        <div><p className="font-semibold">{u.name}</p><p className="text-sm text-muted-foreground">{u.email}</p></div>
                      </div>
                    </td>
                    <td className="p-4">{u.department || 'N/A'}</td>
                    <td className="p-4">
                      <Select value={u.role} onValueChange={v => handleUpdateRole(u.id, v)}>
                        <SelectTrigger className="w-32"><Badge className={getRoleColor(u.role)}>{roles.find(r => r.value === u.role)?.label}</Badge></SelectTrigger>
                        <SelectContent>{roles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-4"><div className="flex items-center gap-1"><Star className="w-4 h-4 text-yellow-500" /><span className="font-bold">{u.points}</span></div></td>
                    <td className="p-4"><Button size="sm" variant="outline" className="border-2 border-black" onClick={() => { setSelectedUser(u); setShowRecommend(true); }}><Award className="w-4 h-4 mr-1" />Recommend</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card-sketchy text-center py-16">
            <Users className="w-20 h-20 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-sketch text-2xl">No users found</h3>
          </div>
        )}
      </div>
    </Layout>
  );
}
